import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Box, Breadcrumbs, Button, Card, CardContent, Typography } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumGrupoDocumento, EnumTipoDocumento } from '../../api'
import { DocumentModel } from '../../api/Argentia/endpoints/documents/model/document'
import { EnumCommodity } from '../../api/Argentia/enums'
import Page from '../../components/page/page'
import PageCenterColumn from '../../components/page/pageCenterColumn'
import PageHeader from '../../components/page/pageHeader'
import {
  AttachFileDialog,
  AttachFileDialogRef
} from '../../components/shared/documents/attachFileDialog'
import { LoadingCard } from '../../components/shared/loadingCard/loadingCard'
import { enumEnumToValueNumber, enumKeyToValueString, enumToSelectOptionArray } from '../../helpers'
import { fileDownloadHelper } from '../../helpers/fileDownloadHelper'
import { FormDialogDefinitionProps, useConfirmationDialog, useFormDialog } from '../../hooks'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { getCustomerDomain } from '../../redux'
import { mapAttachedDocumentFileItemToDocument } from '../procedures/procedures.mapper'
import { AttachedDocumentFile } from '../procedures/procedures.types'
import { mapFormDataToPatchRequest, mapFormDataToRequest } from './documents.mapper'
import { EnumDocumentsNavigationFrom } from './documents.model'
import {
  createDocument,
  deleteDocument,
  downloadDocument,
  fetchDocumentsByElementId,
  fetchDocumentsTypesByDocumentGroup,
  updateDocument
} from './documents.service'

type DocumentsPageProps = {
  location: {
    state: {
      elementId: number // can be a contractId, tradeId
      elementCode: string // idem above
      commodity: EnumCommodity
      navigation: EnumDocumentsNavigationFrom
    }
  }
}

export const DocumentsPage: React.FC<DocumentsPageProps> = props => {
  const [documentsData, setDocumentsData] = useState<DocumentModel[]>([])
  const [documentsTypes, setDocumentsTypes] = useState<SelectOption[]>([])

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isLoadingDocumentsTypes, setIsLoadingDocumentsTypes] = useState<boolean>(false)
  const [showAttachmentDialog, setShowAttachmentDialog] = useState<boolean>(false)

  const {
    openForm,
    updateFormDefinition,
    getFormData,
    updateFormData,
    closeForm,
    formProps,
    CustomFormDialog,
    setFormWaiting
  } = useFormDialog()
  const { info, error } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const [t] = useTranslation(['globalerisLibrary', 'documents', 'enums'])
  const history = useHistory()

  const attachFileDialogRef = useRef<AttachFileDialogRef>(null)

  if (props.location.state === undefined) history.goBack()

  const elementId = props.location.state.elementId
  const elementCode = props.location.state.elementCode
  const navigation = props.location.state.navigation
  const breadcrumbNavigation = enumKeyToValueString(
    EnumDocumentsNavigationFrom,
    props.location.state.navigation
  )
  const commodity = enumEnumToValueNumber(EnumCommodity, props.location.state.commodity)

  // #region Form Definition Starts
  const formEditDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        sections: [
          {
            inputs: [
              {
                name: 'type',
                label: t('documents:forms.add.inputs.type'),
                type: DataType.SELECT,
                selectOptions: documentsTypes,
                isLoading: isLoadingDocumentsTypes,
                required: true
              },
              {
                name: 'description',
                label: t('documents:forms.add.inputs.description'),
                type: DataType.TEXT,
                required: true
              },
              {
                name: 'comments',
                label: t('documents:forms.add.inputs.comments'),
                type: DataType.TEXT
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingDocumentsTypes, documentsTypes])

  // #endregion Form Definition Ends

  // #region Handlers Starts

  const onResetDialog = () => {
    attachFileDialogRef?.current && attachFileDialogRef?.current?.reset()
  }

  const handleEditDialogOpen = async selectedRow => {
    openForm({
      definition: formEditDefinition,
      title: t('documents:forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: selectedRow,
      onClose: onCloseEditHandler
    })
    return true
  }

  const onCloseEditHandler = async formData => {
    if (formData) {
      const request = mapFormDataToPatchRequest(formData)
      // PATCH form Data
      await updateDocument(request).then(response => {
        if (response.success) {
          info(t('documents:notifications.update.success'))
          getDocumentsByElementId(elementId, navigation)
        } else {
          error(t('documents:notifications.update.error'))
        }
      })
    }
    closeForm()
  }

  const handleDelete = async (selectedRow: DocumentModel) => {
    getConfirmation(ConfirmationDialogs.DELETE).then(async () => {
      await deleteDocument(selectedRow.id!).then(response => {
        if (response.success) {
          info(t('documents:notifications.delete.success'))
          getDocumentsByElementId(elementId, navigation)
        } else {
          error(t('documents:notifications.delete.error'))
        }
      })
      closeConfirmation()
    })
  }

  const handleInsert = async () => {
    handleOpenDialog()
    return true
  }

  const handleCloseDialog = () => {
    onResetDialog()
    setShowAttachmentDialog(false)
  }

  const handleOpenDialog = () => {
    setShowAttachmentDialog(true)
  }

  const handleAddDocument = async (document: AttachedDocumentFile, reopen?: boolean) => {
    await mapAttachedDocumentFileItemToDocument(document).then(async document => {
      const request = mapFormDataToRequest(document, navigation, elementId)
      return await createDocument(request)
        .then(response => {
          if (response.success) {
            info(t('documents:notifications.create.success'))
            getDocumentsByElementId(elementId, navigation)
          } else {
            error(t('documents:notifications.create.error'))
          }
        })
        .finally(() => {})
    })
    handleCloseDialog()
    reopen &&
      setTimeout(() => {
        handleOpenDialog()
      }, 500)
  }

  const handleAddAndNewDocument = (document: AttachedDocumentFile) => {
    handleAddDocument(document, true)
  }

  const handleDownloadFile = async (row: DocumentModel) => {
    if (row.id) {
      await downloadDocument(row.id).then(response => {
        if (response.success && response.data) {
          fileDownloadHelper(response.data.name, response.data)
        } else {
          error(t('documents:notifications.downloadFiles.error'))
        }
      })
    }
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const getDocumentsByElementId = async (
    elementId: number,
    element: EnumDocumentsNavigationFrom
  ) => {
    setIsFetching(true)
    await fetchDocumentsByElementId(elementId, element).then(response => {
      if (response.success && response.data != undefined) {
        setDocumentsData(response.data)
        setIsFetching(false)
      }
    })
  }
  // #endregion Functions Ends

  // #region DataTable Config Starts
  const tableColumns: DataTableColumn[] = [
    {
      name: 'id',
      type: DataType.NUMBER,
      display: false
    },
    {
      name: 'documentDate',
      label: t('documents:dataTable.columns.documentDate'),
      type: DataType.DATE
    },
    {
      name: 'type',
      label: t('documents:dataTable.columns.type'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoDocumento, 'enums:EnumTipoDocumento')
    },
    {
      name: 'description',
      label: t('documents:dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'comments',
      label: t('documents:dataTable.columns.comments'),
      type: DataType.TEXT
    }
  ]

  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    analytics: {
      customer: getCustomerDomain(),
      view: t('documents:dataTable.analytics')
    },
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: navigation === EnumDocumentsNavigationFrom.Procedures ? false : true,
    // customInsertEditModal: true,
    // ...(navigation != EnumDocumentsNavigationFrom.Contracts &&
    //   navigation != EnumDocumentsNavigationFrom.Trades && { customInsertEditModal: true }),
    customInsertEditModal: navigation === EnumDocumentsNavigationFrom.Procedures ? false : true,
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,
    onRowInsert: handleInsert,
    rowCustomActions: [
      {
        icon: <CloudDownloadIcon fontSize="small" />,
        tooltip: t('documents:dataTable.rowCustomActions.download'),
        onClick: handleDownloadFile
      },
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('documents:dataTable.rowCustomActions.edit'),
        onClick: handleEditDialogOpen
      },
      {
        icon: <Delete fontSize="small" />,
        tooltip: t('documents:dataTable.rowCustomActions.delete'),
        onClick: handleDelete
      }
    ]
  }

  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    const getDocumentsTypesByDocumentGroup = async (documentGroupId: number) => {
      setIsLoadingDocumentsTypes(true)
      await fetchDocumentsTypesByDocumentGroup(documentGroupId).then(response => {
        if (response.success && response.data != undefined) {
          setDocumentsTypes(response.data)
          setIsLoadingDocumentsTypes(false)
        } else {
          error(t('documents:notifications.getDocuments.error'))
        }
      })
    }

    getDocumentsByElementId(elementId, navigation)

    commodity === EnumCommodity.GasNatural
      ? getDocumentsTypesByDocumentGroup(EnumGrupoDocumento.DocumentosAdjuntosGas)
      : getDocumentsTypesByDocumentGroup(EnumGrupoDocumento.DocumentosAdjuntosElectricidad)
  }, [])

  useEffect(() => {
    updateFormDefinition(formEditDefinition)
  }, [formEditDefinition])
  // #endregion Hooks Ends

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('documents:title')}>
        <Button
          variant="text"
          color="primary"
          onClick={() => {
            history.goBack()
          }}
        >
          {t('documents:actions.back')}
        </Button>
      </PageHeader>
      <PageCenterColumn>
        <Box mb={1}>
          <Breadcrumbs separator=">" aria-label="breadcrumb">
            <Typography key="breadcrumb_1" color="primary" variant="subtitle2">
              {t(`documents:breadcrumbs.${breadcrumbNavigation}`)}
            </Typography>
            <Typography key="breadcrumb_2" color="primary" variant="subtitle2">
              {elementCode}
            </Typography>
            <Typography key="breadcrumb_3" color="primary" variant="subtitle1">
              {t('documents:breadcrumbs.documents')}
            </Typography>
          </Breadcrumbs>
        </Box>
        {isFetching ? (
          <LoadingCard />
        ) : (
          <Card raised>
            <CardContent>
              <DataTable
                title={t('documents:dataTable.title')}
                data={documentsData}
                columns={tableColumns}
                options={tableOptions}
              />
            </CardContent>
          </Card>
        )}
        <AttachFileDialog
          ref={attachFileDialogRef}
          open={showAttachmentDialog}
          documentTypes={documentsTypes}
          onClose={handleCloseDialog}
          onSave={handleAddDocument}
          onSaveAndNew={handleAddAndNewDocument}
        />
        <CustomFormDialog {...formProps} />
      </PageCenterColumn>
    </Page>
  )
}
